import React, { useState, useEffect } from 'react';
import { Layout, Menu, Card, Row, Col, Statistic, Avatar, Dropdown, Button, Typography, List, Tag, message, Progress, Modal, Form, Input, DatePicker, Upload } from 'antd';
import dayjs from 'dayjs';
import { 
  DashboardOutlined, 
  UserOutlined, 
  TeamOutlined, 
  LogoutOutlined,
  BankOutlined,
  BookOutlined,
  TrophyOutlined,
  RiseOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
  ExclamationCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  EditOutlined,
  CameraOutlined,
  SettingOutlined,
  BellOutlined,
  SunOutlined,
  MoonOutlined,
  UploadOutlined,
  SoundOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  AreaChart,
  Area,
  RadialBarChart,
  RadialBar,
  Legend
} from 'recharts';
import { authAPI, User } from '../services/api';
import { systemSettingsAPI, SystemSettings } from '../services/systemSettingsAPI';
import { companyAPI, UserCompany, Company } from '../services/companyAPI';
import { studentAPI, StudentStatistics } from '../services/studentAPI';
import { teacherAPI, TeacherStatistics } from '../services/teacherAPI';
import { courseAPI } from '../services/courseAPI';
import { paymentAPI, PaymentStatistics } from '../services/paymentAPI';
import StudentList from './Students/StudentList';
import CompaniesManagement from './Companies/CompaniesManagement';
import TeachersList from './Teachers/TeachersList';
import SpecialtiesList from './Specialties/SpecialtiesList';
import CoursesList from './Courses/CoursesList';
import PaymentList from './Payments/PaymentList';
import OverduePaymentsList from './OverduePayments/OverduePaymentsList';
import SystemLogo from './SystemLogo';
import UserManagement from './UserManagement/UserManagement';
import PermissionManagement from './Permissions/PermissionManagement';
import { usePermissions } from '../contexts/PermissionContext';
import AdvertisementsList from './Advertisements/AdvertisementsList';
import NotificationsList from './Notifications/NotificationsList';

// Union tip yaradırıq
type SelectedCompany = UserCompany | Company;

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

// Custom colors for charts
const CHART_COLORS = {
  primary: '#1890ff',
  success: '#52c41a', 
  warning: '#faad14',
  error: '#ff4d4f',
  purple: '#722ed1',
  cyan: '#13c2c2',
  orange: '#fa8c16',
  pink: '#eb2f96',
  lime: '#a0d911',
  gold: '#fadb14'
};

// const GRADIENT_COLORS = [
//   { start: '#667eea', end: '#764ba2' },
//   { start: '#f093fb', end: '#f5576c' },
//   { start: '#4facfe', end: '#00f2fe' },
//   { start: '#43e97b', end: '#38f9d7' },
//   { start: '#fa709a', end: '#fee140' },
//   { start: '#a8edea', end: '#fed6e3' },
//   { start: '#ffecd2', end: '#fcb69f' },
//   { start: '#ff9a9e', end: '#fecfef' }
// ];

interface DashboardProps {
  user: User;
  onLogout: () => void;
  onUserUpdate?: (user: User) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout, onUserUpdate }) => {
  const { hasPermission, refreshPermissions } = usePermissions();

  // Dashboard yüklənəndə icazələri yüklə
  useEffect(() => {
    refreshPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Component yüklənəndə favicon-u dərhal yenilə
  useEffect(() => {
    // İlk yükləmədə default favicon-u təyin et
    updateFavicon();
  }, []);
  const [activeTab, setActiveTab] = useState(() => {
    // localStorage-dan aktiv tab-ı oxu
    return localStorage.getItem('crm_active_tab') || 'dashboard';
  });
  const [collapsed, setCollapsed] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<SelectedCompany | null>(null);
  const [userCompanies, setUserCompanies] = useState<UserCompany[]>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [statistics, setStatistics] = useState<StudentStatistics | null>(null);
  const [teacherStatistics, setTeacherStatistics] = useState<TeacherStatistics | null>(null);
  const [courseStatistics, setCourseStatistics] = useState<any>(null);
  const [paymentStatistics, setPaymentStatistics] = useState<PaymentStatistics | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(() => {
    // Default olaraq hazırki ili seç
    return new Date().getFullYear();
  });
  const [isProfileModalVisible, setIsProfileModalVisible] = useState(false);
  const [profileForm] = Form.useForm();
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // localStorage-dan dark mode parametrini oxu
    const saved = localStorage.getItem('crm_dark_mode');
    return saved ? JSON.parse(saved) : false;
  });
  const [notificationSettings, setNotificationSettings] = useState(() => {
    // localStorage-dan notification ayarlarını oxu
    const saved = localStorage.getItem('crm_notification_settings');
    return saved ? JSON.parse(saved) : {
      smsBeforePayment: {
        active: true,
        daysBefore: 3,
        message: "Hormetli {student_name}, {payment_date} tarixinde {amount} AZN mebleghinde odenishiniz var. Vaxtinda odemeyi unutmayin."
      },
      whatsappBeforePayment: {
        active: true,
        daysBefore: 3,
        message: "Salam {student_name}! 🎓\n\n{payment_date} tarixinde {amount} AZN mebleghinde odenishiniz var.\n\nVaxtinda odemeyi unutmayin! 💳"
      },
      smsOnPaymentDay: {
        active: true,
        message: "Hormetli {student_name}, bu gun {amount} AZN mebleghinde odenishinizin son gunudur. Zehmet olmasa odemeyi heyata kechirin."
      },
      whatsappOnPaymentDay: {
        active: true,
        message: "Salam {student_name}! 🎓\n\nBu gun {amount} AZN mebleghinde odenishinizin son gunudur. ⏰\n\nZehmet olmasa odemeyi heyata kechirin! 💳"
      }
    };
  });
  const [tempNotificationSettings, setTempNotificationSettings] = useState(notificationSettings);
  const [systemSettings, setSystemSettings] = useState<SystemSettings | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: 'Ödəniş xatırlatması',
      message: 'Ayxan Mustafayev - 3 gün sonra 150 AZN ödəniş',
      time: '2 saat əvvəl',
      read: false,
      type: 'warning'
    },
    {
      id: 2,
      title: 'Ödəniş xatırlatması',
      message: 'Leyla Həsənova - Bu gün 200 AZN ödəniş',
      time: '5 saat əvvəl',
      read: false,
      type: 'info'
    },
    {
      id: 3,
      title: 'SMS göndərildi',
      message: '15 tələbəyə ödəniş xatırlatması SMS-i göndərildi',
      time: '1 gün əvvəl',
      read: true,
      type: 'success'
    }
  ]);

  useEffect(() => {
    loadUserCompanies();
    // Yalnız admin istifadəçilər üçün bütün şirkətləri yüklə
    if (user.is_superuser) {
      loadAllCompanies();
    }
    loadSystemSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Notification ayarları yüklənəndə temp state-i sync et
  useEffect(() => {
    setTempNotificationSettings(notificationSettings);
  }, [notificationSettings]);
  // Notification ayarlarını saxla
  const saveNotificationSettings = () => {
    setNotificationSettings(tempNotificationSettings);
    localStorage.setItem('crm_notification_settings', JSON.stringify(tempNotificationSettings));
    message.success('Bildiriş ayarları saxlanıldı');
  };

  // Dəyişiklikləri ləğv et
  const cancelNotificationChanges = () => {
    setTempNotificationSettings(notificationSettings);
    message.info('Dəyişikliklər ləğv edildi');
  };

  // Dəyişiklik olub-olmadığını yoxla
  const hasNotificationChanges = JSON.stringify(notificationSettings) !== JSON.stringify(tempNotificationSettings);

  // Sistem ayarlarını yüklə
  const loadSystemSettings = async () => {
    try {
      const response = await systemSettingsAPI.getSettings();
      if (response.success && response.settings) {
        setSystemSettings(response.settings);
      }
    } catch (error) {
      console.error('System settings load error:', error);
    }
  };

  // Logo yüklə
  const handleLogoUpload = async (file: File) => {
    try {
      setLogoUploading(true);
      const response = await systemSettingsAPI.uploadLogo(file);
      
      if (response.success) {
        message.success('Logo yükləndi');
        setLogoFile(null);
        await loadSystemSettings(); // Yenidən yüklə
      } else {
        message.error(response.message || 'Logo yüklənmədi');
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      message.error('Logo yüklənmədi');
    } finally {
      setLogoUploading(false);
    }
  };

  // Logo seçimi
  const handleLogoSelect = (file: File) => {
    // Fayl formatını yoxla
    if (!file.type.startsWith('image/')) {
      message.error('Yalnız şəkil faylları seçə bilərsiniz');
      return false;
    }

    // Fayl ölçüsünü yoxla (5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error('Logo faylı 5MB-dan böyük ola bilməz');
      return false;
    }

    setLogoFile(file);
    return false; // Upload-u manual idarə edirik
  };

  // SMS test funksiyası
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const testSMS = async () => {
    const testMessage = tempNotificationSettings.smsBeforePayment.message
      .replace('{student_name}', 'Ruslan Abuşov')
      .replace('{payment_date}', '15.02.2026')
      .replace('{amount}', '150');
    
    try {
      // Loading mesajı göstər
      message.loading('SMS göndərilir...', 0);
      
      // Backend API çağırışı
      const result = await authAPI.sendSMSTest(testMessage, '+994556772714');
      
      // Loading mesajını bağla
      message.destroy();
      
      if (result.success) {
        Modal.success({
          title: 'SMS Göndərildi',
          content: (
            <div>
              <p className="mb-2">✅ Test SMS uğurla göndərildi!</p>
              <p className="text-sm text-gray-600">Telefon: +994556772714</p>
              <p className="text-sm text-gray-600">SMS sayı: {Math.ceil(testMessage.length / 160)}</p>
              <div className="bg-gray-100 p-3 rounded mt-3">
                <p className="text-sm font-medium mb-1">Göndərilən mesaj:</p>
                <p className="text-sm">{testMessage}</p>
              </div>
            </div>
          ),
        });
      } else {
        Modal.error({
          title: 'SMS Göndərilmədi',
          content: (
            <div>
              <p className="mb-2">❌ SMS göndərilmədi</p>
              <p className="text-sm text-red-600">{result.message || 'Xəta baş verdi'}</p>
            </div>
          ),
        });
      }
      
    } catch (error) {
      message.destroy();
      console.error('SMS test xətası:', error);
      Modal.error({
        title: 'Xəta',
        content: 'SMS göndərilmədi. Xəta baş verdi.',
      });
    }
  };

  // WhatsApp test funksiyası
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const testWhatsApp = async () => {
    const testMessage = tempNotificationSettings.whatsappBeforePayment.message
      .replace('{student_name}', 'Test İstifadəçi')
      .replace('{payment_date}', '15.02.2026')
      .replace('{amount}', '150');
    
    try {
      // Loading mesajı göstər
      message.loading('WhatsApp mesajı göndərilir...', 0);
      
      // Backend API çağırışı
      const result = await authAPI.sendWhatsAppTest(testMessage, '+994556772714');
      
      // Loading mesajını bağla
      message.destroy();
      
      if (result.success) {
        if (result.test_mode) {
          message.info('Test rejimi: WhatsApp API konfiqurasiyası yoxdur. Real mesaj göndərilmədi.');
        } else {
          message.success('WhatsApp mesajı uğurla göndərildi! +994556772714 nömrəsinə mesaj çatdı.');
        }
      } else {
        message.error(result.message || 'WhatsApp mesajı göndərilmədi');
      }
      
    } catch (error) {
      message.destroy();
      console.error('WhatsApp test xətası:', error);
      message.error('WhatsApp mesajı göndərilmədi. Xəta baş verdi.');
    }
  };
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    localStorage.setItem('crm_dark_mode', JSON.stringify(newDarkMode));
    
    // Body elementinə dark class əlavə et/sil
    if (newDarkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  };

  // Component yüklənəndə dark mode-u tətbiq et
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [isDarkMode]);
  useEffect(() => {
    // Profil şəkli yeniləndi
  }, [user]);

  useEffect(() => {
    if (selectedCompany) {
      const yearParam = selectedYear ?? undefined;
      loadStatistics(yearParam);
      loadTeacherStatistics();
      loadCourseStatistics();
      loadPaymentStatistics();
      
      // Favicon-u yenilə
      const logoUrl = (selectedCompany as any).logo_url || (selectedCompany as any).logo;
      updateFavicon(logoUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, selectedYear]);

  // Component yüklənəndə favicon-u yenilə
  useEffect(() => {
    if (selectedCompany) {
      const logoUrl = (selectedCompany as any).logo_url || (selectedCompany as any).logo;
      updateFavicon(logoUrl);
    } else {
      // Şirkət seçilməyibsə default favicon
      updateFavicon();
    }
  }, [selectedCompany]);

  const loadStatistics = async (year?: number) => {
    if (!selectedCompany) return;
    
    try {
      const response = await studentAPI.getStatistics(selectedCompany.id, year);
      if (response.success && response.data) {
        setStatistics(response.data);
        
        // İlk dəfə yüklənəndə mövcud illərdən birini seç
        if (!selectedYear && response.data.available_years && response.data.available_years.length > 0) {
          setSelectedYear(response.data.available_years[0]);
        }
      }
    } catch (error) {
      console.error('Statistikalar yüklənmədi:', error);
    }
  };

  const loadTeacherStatistics = async () => {
    if (!selectedCompany) return;
    
    try {
      const response = await teacherAPI.getStatistics(selectedCompany.id);
      if (response.success && response.data) {
        setTeacherStatistics(response.data);
      }
    } catch (error) {
      console.error('Müəllim statistikaları yüklənmədi:', error);
    }
  };

  const loadPaymentStatistics = async () => {
    if (!selectedCompany) return;
    
    try {
      const response = await paymentAPI.getStatistics(selectedCompany.id, selectedYear || undefined);
      if (response.success && response.data) {
        setPaymentStatistics(response.data);
      }
    } catch (error) {
      console.error('Ödəniş statistikaları yüklənmədi:', error);
    }
  };

  const loadCourseStatistics = async () => {
    if (!selectedCompany) return;
    
    try {
      const response = await courseAPI.getCourseStatistics(selectedCompany.id);
      if (response.success && response.data) {
        setCourseStatistics(response.data);
      }
    } catch (error) {
      console.error('Kurs statistikaları yüklənmədi:', error);
    }
  };

  const loadAllCompanies = async () => {
    try {
      const response = await companyAPI.getCompanies();
      if (response.success && response.data) {
        setAllCompanies(response.data);
        
        // Əgər istifadəçinin şirkəti yoxdursa və admin-dirsə, localStorage-dan yoxla
        if (userCompanies.length === 0 && user.is_superuser) {
          const savedCompanyStr = localStorage.getItem('crm_selected_company');
          if (savedCompanyStr) {
            try {
              const savedCompany = JSON.parse(savedCompanyStr);
              const validCompany = response.data.find(c => c.id === savedCompany.id);
              if (validCompany && !selectedCompany) {
                setSelectedCompany(validCompany);
              }
            } catch (error) {
              console.error('localStorage parse error:', error);
            }
          }
        }
      } else {
        console.error('Failed to load all companies:', response.message);
      }
    } catch (error) {
      console.error('Bütün şirkətlər yüklənmədi:', error);
    }
  };

  const loadUserCompanies = async () => {
    try {
      const response = await companyAPI.getUserCompanies();
      if (response.success && response.data) {
        setUserCompanies(response.data);
        
        // localStorage-dan şirkəti yoxla və təsdiq et
        const savedCompanyStr = localStorage.getItem('crm_selected_company');
        if (savedCompanyStr) {
          try {
            const savedCompany = JSON.parse(savedCompanyStr);
            // Saxlanmış şirkətin istifadəçinin şirkətləri arasında olub-olmadığını yoxla
            const validCompany = response.data.find(c => c.id === savedCompany.id);
            if (validCompany) {
              setSelectedCompany(validCompany);
            } else {
              // Əgər saxlanmış şirkət artıq mövcud deyilsə, localStorage-ı təmizlə
              localStorage.removeItem('crm_selected_company');
              // İlk mövcud şirkəti seç
              if (response.data.length > 0) {
                const firstCompany = response.data[0];
                setSelectedCompany(firstCompany);
                localStorage.setItem('crm_selected_company', JSON.stringify(firstCompany));
              }
            }
          } catch (error) {
            // JSON parse xətası varsa, localStorage-ı təmizlə
            localStorage.removeItem('crm_selected_company');
            if (response.data.length > 0) {
              const firstCompany = response.data[0];
              setSelectedCompany(firstCompany);
              localStorage.setItem('crm_selected_company', JSON.stringify(firstCompany));
            }
          }
        } else {
          // Əgər heç şirkət seçilməyibsə, ilk şirkəti seç
          if (response.data.length > 0) {
            const firstCompany = response.data[0];
            setSelectedCompany(firstCompany);
            localStorage.setItem('crm_selected_company', JSON.stringify(firstCompany));
          }
        }
      } else {
        console.error('Failed to load companies:', response.message);
      }
    } catch (error) {
      console.error('Şirkətlər yüklənmədi:', error);
    }
  };

  // Əgər istifadəçinin şirkəti yoxdursa və admin-dirsə, bütün şirkətlərdən birini seç
  useEffect(() => {
    if (!selectedCompany && user.is_superuser && allCompanies.length > 0 && userCompanies.length === 0) {
      const firstCompany = allCompanies[0];
      setSelectedCompany(firstCompany);
      localStorage.setItem('crm_selected_company', JSON.stringify(firstCompany));
    }
  }, [allCompanies, userCompanies, selectedCompany, user.is_superuser]);

  // Favicon dəyişdirmə funksiyası
  const updateFavicon = (logoUrl?: string) => {
    try {
      // Bütün favicon linklərini tap və sil
      const existingLinks = document.querySelectorAll("link[rel*='icon']");
      existingLinks.forEach(link => link.remove());
      
      // Yeni favicon linki yarat
      const faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      faviconLink.type = 'image/svg+xml';
      
      if (logoUrl) {
        // Şirkət logosu varsa onu istifadə et (cache bypass üçün timestamp əlavə et)
        faviconLink.href = `${logoUrl}?t=${Date.now()}`;
      } else {
        // Default favicon istifadə et (cache bypass üçün timestamp əlavə et)
        faviconLink.href = `/favicon.svg?t=${Date.now()}`;
      }
      
      // Head-ə əlavə et
      document.head.appendChild(faviconLink);
      
      // Əlavə olaraq ICO formatı da əlavə et (fallback üçün)
      const icoLink = document.createElement('link');
      icoLink.rel = 'alternate icon';
      icoLink.type = 'image/x-icon';
      icoLink.href = `/favicon.ico?t=${Date.now()}`;
      document.head.appendChild(icoLink);
    } catch (error) {
      console.error('Favicon update error:', error);
    }
  };

  const handleCompanyChange = async (companyId: number) => {
    // Əvvəlcə istifadəçinin şirkətlərindən axtar
    let company: SelectedCompany | undefined = userCompanies.find(c => c.id === companyId);
    
    // Əgər tapılmadısa və admin-dirsə, bütün şirkətlərdən axtar
    if (!company && user.is_superuser) {
      company = allCompanies.find(c => c.id === companyId);
    }
    
    // Əgər şirkət tapılmadısa və ya icazə yoxdursa
    if (!company) {
      message.error('Bu şirkətə giriş icazəniz yoxdur');
      return;
    }
    
    if (company) {
      try {
        // Backend-ə company switch sorğusu göndər
        const response = await authAPI.switchCompany(companyId);
        if (response.success) {
          setSelectedCompany(company);
          localStorage.setItem('crm_selected_company', JSON.stringify(company));
          
          // Favicon-u yenilə
          const logoUrl = (company as any).logo_url || (company as any).logo;
          updateFavicon(logoUrl);
          
          message.success(`${company.name} seçildi`);
          // Statistikaları yenilə
          const yearParam = selectedYear ?? undefined;
          loadStatistics(yearParam);
          loadTeacherStatistics();
          loadCourseStatistics();
          loadPaymentStatistics();
        } else {
          message.error(response.message || 'Şirkət dəyişdirilmədi');
        }
      } catch (error) {
        console.error('Company switch error:', error);
        message.error('Şirkət dəyişdirilmədi');
      }
    }
  };

  // Tab dəyişəndə localStorage-a yaz
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    localStorage.setItem('crm_active_tab', key);
  };

  // Profil edit modal-ını aç
  const openProfileModal = () => {
    profileForm.setFieldsValue({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
    });
    setIsProfileModalVisible(true);
  };

  // Profil məlumatlarını yenilə
  const handleProfileUpdate = async (values: any) => {
    try {
      // Əvvəlcə profil məlumatlarını yenilə
      const profileResponse = await authAPI.updateProfile(values);
      if (!profileResponse.success) {
        message.error(profileResponse.message || 'Profil yenilənmədi');
        return;
      }

      // Əgər şəkil seçilibsə, onu da yüklə
      if (profileImageFile) {
        await handleProfileImageUpload(profileImageFile);
      }
      
      message.success('Profil məlumatları yeniləndi');
      setIsProfileModalVisible(false);
      setProfileImageFile(null);
      
      // User məlumatlarını yenidən yüklə
      try {
        const userResponse = await authAPI.getProfile();
        if (userResponse.success && userResponse.user) {
          // Parent component-də user state-ini yenilə
          if (onUserUpdate) {
            onUserUpdate(userResponse.user);
          } else {
            // Əgər callback yoxdursa, səhifəni yenilə
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('User profile reload error:', error);
        window.location.reload();
      }
    } catch (error) {
      console.error('Profile update error:', error);
      message.error('Profil yenilənmədi');
    }
  };

  // Profil şəkli seçimi
  const handleProfileImageSelect = (file: File) => {
    // Şəkil formatını yoxla
    if (!file.type.startsWith('image/')) {
      message.error('Yalnız şəkil faylları yükləyə bilərsiniz');
      return false;
    }
    
    // Fayl ölçüsünü yoxla (5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error('Şəkil ölçüsü 5MB-dan çox ola bilməz');
      return false;
    }
    
    setProfileImageFile(file);
    return false; // Upload-u manual idarə edirik
  };

  // Profil şəkli yükləmə
  const handleProfileImageUpload = async (file: File) => {
    try {
      const response = await authAPI.uploadProfileImage(file);
      if (response.success) {
        message.success('Profil şəkli yükləndi');
        return response;
      } else {
        message.error(response.message || 'Şəkil yüklənmədi');
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Profile image upload error:', error);
      message.error('Şəkil yüklənmədi');
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      // Logout zamanı localStorage-ı təmizlə
      localStorage.removeItem('crm_active_tab');
      localStorage.removeItem('crm_selected_company');
      // Favicon-u default vəziyyətə qaytar
      updateFavicon();
      onLogout();
    } catch (error) {
      console.error('Çıxış xətası:', error);
      localStorage.removeItem('crm_active_tab');
      localStorage.removeItem('crm_selected_company');
      // Favicon-u default vəziyyətə qaytar
      updateFavicon();
      onLogout();
    }
  };

  const menuItems = [
    // Dashboard - hamı görə bilər
    { key: 'dashboard', icon: <DashboardOutlined />, label: 'Ana Səhifə' },
    
    // Tələbələr
    ...(user.is_superuser || hasPermission('view_students') ? [
      { key: 'students', icon: <TeamOutlined />, label: 'Tələbələr' }
    ] : []),
    
    // Ödənişlər
    ...(user.is_superuser || hasPermission('view_payments') ? [
      { key: 'payments', icon: <DollarOutlined />, label: 'Ödənişlər' }
    ] : []),
    
    // Gecikmiş ödənişlər
    ...(user.is_superuser || hasPermission('view_overdue_payments') ? [
      { key: 'overdue-payments', icon: <ExclamationCircleOutlined />, label: 'Gecikmiş Ödənişlər' }
    ] : []),
    
    // Reklamlar
    ...(user.is_superuser || hasPermission('view_advertisements') ? [
      { key: 'advertisements', icon: <SoundOutlined />, label: 'Reklamlar' }
    ] : []),
    
    // Bildirişlər
    ...(user.is_superuser || hasPermission('view_notifications') ? [
      { key: 'notifications', icon: <BellOutlined />, label: 'Bildirişlər' }
    ] : []),
    
    // Müəllimlər
    ...(user.is_superuser || hasPermission('view_teachers') ? [
      { key: 'teachers', icon: <UserOutlined />, label: 'Müəllimlər' }
    ] : []),
    
    // İxtisaslar
    ...(user.is_superuser || hasPermission('view_specialties') ? [
      { key: 'specialties', icon: <BookOutlined />, label: 'İxtisaslar' }
    ] : []),
    
    // Kurslar
    ...(user.is_superuser || hasPermission('view_courses') ? [
      { key: 'courses', icon: <TrophyOutlined />, label: 'Kurslar' }
    ] : []),
    
    // Şirkətlər - yalnız icazəsi olanlar
    ...(user.is_superuser || hasPermission('view_companies') ? [
      { key: 'companies', icon: <BankOutlined />, label: 'Şirkətlər' }
    ] : []),
    
    // İstifadəçi İdarəetməsi - yalnız icazəsi olanlar
    ...(user.is_superuser || hasPermission('view_users') ? [
      { key: 'user-management', icon: <UserOutlined />, label: 'İstifadəçi İdarəetməsi' }
    ] : []),
    
    // İcazə İdarəetməsi - yalnız icazəsi olanlar
    ...(user.is_superuser || hasPermission('view_permissions') ? [
      { key: 'permissions', icon: <SettingOutlined />, label: 'İcazə İdarəetməsi' }
    ] : []),
  ];

  // Chart rəngləri
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Chart məlumatlarını hazırla
  const prepareChartData = () => {
    if (!statistics) return { 
      statusData: [], 
      monthlyData: [],
      performanceData: [],
      monthlyRevenueData: []
    };

    // Status məlumatları
    const statusData = [
      { 
        name: 'Aktiv', 
        value: statistics.active_students, 
        fill: CHART_COLORS.success,
        percentage: ((statistics.active_students / statistics.total_students) * 100).toFixed(1)
      },
      { 
        name: 'Digər', 
        value: statistics.total_students - statistics.active_students, 
        fill: CHART_COLORS.error,
        percentage: (((statistics.total_students - statistics.active_students) / statistics.total_students) * 100).toFixed(1)
      }
    ].filter(item => item.value > 0);

    // Aylıq məlumatlar (nümunə məlumat)
    const monthlyData = [
      { month: 'Yanvar', students: Math.floor(statistics.total_students * 0.7), growth: 5 },
      { month: 'Fevral', students: Math.floor(statistics.total_students * 0.75), growth: 7 },
      { month: 'Mart', students: Math.floor(statistics.total_students * 0.8), growth: 6 },
      { month: 'Aprel', students: Math.floor(statistics.total_students * 0.85), growth: 8 },
      { month: 'May', students: Math.floor(statistics.total_students * 0.9), growth: 6 },
      { month: 'İyun', students: statistics.total_students, growth: 11 }
    ];

    // Performance data for radial chart
    const performanceData = [
      {
        name: 'Aktiv Tələbələr',
        value: (statistics.active_students / statistics.total_students) * 100,
        fill: CHART_COLORS.success
      }
    ];

    // Aylıq gəlir məlumatları - backend-dən gələn real məlumatlar
    const monthlyRevenueData = statistics.monthly_revenue || [];

    return { statusData, monthlyData, performanceData, monthlyRevenueData };
  };

  const { statusData, monthlyData, performanceData, monthlyRevenueData } = prepareChartData();

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profil',
      onClick: () => handleTabChange('profile'),
    },
    {
      key: 'theme-toggle',
      icon: isDarkMode ? <SunOutlined /> : <MoonOutlined />,
      label: isDarkMode ? 'Açıq tema' : 'Qaranlıq tema',
      onClick: toggleDarkMode,
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Parametrlər',
      onClick: () => handleTabChange('settings'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Çıxış',
      onClick: handleLogout,
    },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'students':
        if (!user.is_superuser && !hasPermission('view_students')) {
          return <div className="text-center py-12"><Title level={2}>Giriş rədd edildi</Title><Text type="secondary">Bu bölməyə giriş icazəniz yoxdur.</Text></div>;
        }
        return <StudentList selectedCompany={selectedCompany} />;
      case 'payments':
        if (!user.is_superuser && !hasPermission('view_payments')) {
          return <div className="text-center py-12"><Title level={2}>Giriş rədd edildi</Title><Text type="secondary">Bu bölməyə giriş icazəniz yoxdur.</Text></div>;
        }
        return <PaymentList selectedCompany={selectedCompany} />;
      case 'overdue-payments':
        if (!user.is_superuser && !hasPermission('view_overdue_payments')) {
          return <div className="text-center py-12"><Title level={2}>Giriş rədd edildi</Title><Text type="secondary">Bu bölməyə giriş icazəniz yoxdur.</Text></div>;
        }
        return <OverduePaymentsList />;
      case 'advertisements':
        if (!user.is_superuser && !hasPermission('view_advertisements')) {
          return <div className="text-center py-12"><Title level={2}>Giriş rədd edildi</Title><Text type="secondary">Bu bölməyə giriş icazəniz yoxdur.</Text></div>;
        }
        return <AdvertisementsList />;
      case 'notifications':
        if (!user.is_superuser && !hasPermission('view_notifications')) {
          return <div className="text-center py-12"><Title level={2}>Giriş rədd edildi</Title><Text type="secondary">Bu bölməyə giriş icazəniz yoxdur.</Text></div>;
        }
        return <NotificationsList selectedCompany={selectedCompany} />;
      case 'teachers':
        return <TeachersList selectedCompany={selectedCompany} />;
      case 'specialties':
        return <SpecialtiesList selectedCompany={selectedCompany} />;
      case 'courses':
        return <CoursesList selectedCompany={selectedCompany} />;
      case 'profile':
        return (
          <div>
            <Title level={2} className="mb-6">
              <UserOutlined className="mr-2" />
              İstifadəçi Profili
            </Title>
            
            <Row gutter={[24, 24]}>
              <Col xs={24} lg={8}>
                <Card 
                  title="Profil Məlumatları" 
                  className="shadow-sm"
                  extra={
                    <Button 
                      type="text" 
                      icon={<EditOutlined />} 
                      onClick={openProfileModal}
                      size="small"
                      className="hover:bg-blue-50 hover:text-blue-600 transition-colors rounded-full"
                    />
                  }
                >
                  <div className="text-center mb-6">
                    <Avatar 
                      size={80} 
                      src={user.profile_image ? `${user.profile_image}?t=${Date.now()}` : undefined}
                      className="mb-4"
                      onError={() => {
                        return false;
                      }}
                    >
                      {!user.profile_image && user.first_name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Title level={4} className="mb-2">
                      {user.first_name} {user.last_name}
                    </Title>
                    <Text type="secondary">{user.email}</Text>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <Text strong>İstifadəçi ID:</Text>
                      <Text>{user.id}</Text>
                    </div>
                    <div className="flex justify-between">
                      <Text strong>CRM İstifadəçisi:</Text>
                      <Tag color={user.is_crm_user ? 'green' : 'red'}>
                        {user.is_crm_user ? 'Bəli' : 'Xeyr'}
                      </Tag>
                    </div>
                    <div className="flex justify-between">
                      <Text strong>Admin:</Text>
                      <Tag color={user.is_superuser ? 'blue' : 'default'}>
                        {user.is_superuser ? 'Bəli' : 'Xeyr'}
                      </Tag>
                    </div>
                    {user.current_company && (
                      <div className="flex justify-between">
                        <Text strong>Hazırki Şirkət:</Text>
                        <Text>{user.current_company.name}</Text>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
              
              <Col xs={24} lg={16}>
                <Card title="Şirkətlərim" className="shadow-sm">
                  {userCompanies.length > 0 ? (
                    <List
                      dataSource={userCompanies}
                      renderItem={(company) => (
                        <List.Item
                          actions={[
                            selectedCompany?.id === company.id ? (
                              <Tag color="blue">Aktiv</Tag>
                            ) : (
                              <Button 
                                type="link" 
                                onClick={() => handleCompanyChange(company.id)}
                              >
                                Seç
                              </Button>
                            )
                          ]}
                        >
                          <List.Item.Meta
                            avatar={
                              <Avatar style={{ backgroundColor: '#1890ff' }}>
                                {company.name.charAt(0).toUpperCase()}
                              </Avatar>
                            }
                            title={company.name}
                            description={
                              <div>
                                <div>Rol: <Tag color="green">{company.role}</Tag></div>
                                <div>Şəhər: {company.city}</div>
                                <div>Tələbə sayı: {'student_count' in company ? company.student_count : 0}</div>
                              </div>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <Text type="secondary">Heç bir şirkətə üzv deyilsiniz</Text>
                    </div>
                  )}
                </Card>
                
                
              </Col>
            </Row>
          </div>
        );
      case 'companies':
        // Ancaq admin istifadəçilər üçün
        if (!user.is_superuser) {
          return (
            <div className="text-center py-12">
              <Title level={2} className="mb-4">Giriş rədd edildi</Title>
              <Text type="secondary" className="text-lg">
                Bu bölməyə giriş icazəniz yoxdur.
              </Text>
            </div>
          );
        }
        return <CompaniesManagement />;
      case 'user-management':
        // Ancaq admin istifadəçilər üçün
        if (!user.is_superuser) {
          return (
            <div className="text-center py-12">
              <Title level={2} className="mb-4">Giriş rədd edildi</Title>
              <Text type="secondary" className="text-lg">
                Bu bölməyə giriş icazəniz yoxdur.
              </Text>
            </div>
          );
        }
        return <UserManagement />;
      case 'permissions':
        // Ancaq admin istifadəçilər üçün
        if (!user.is_superuser) {
          return (
            <div className="text-center py-12">
              <Title level={2} className="mb-4">Giriş rədd edildi</Title>
              <Text type="secondary" className="text-lg">
                Bu bölməyə giriş icazəniz yoxdur.
              </Text>
            </div>
          );
        }
        return <PermissionManagement />;
      case 'dashboard':
        return (
          <div>
            <div className="mb-8">
              
            </div>
            
            {selectedCompany && statistics ? (
              <>
                {/* İl Seçici */}
                

                {/* Statistika Kartları - Sadə Dizayn */}
                <Row gutter={[24, 24]} className="mb-8">
                  <Col xs={24} sm={12} lg={6}>
                    <Card >
                      <Statistic
                        title="Aktiv Tələbələr"
                        value={statistics.active_students}
                        prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card >
                      <Statistic
                        title="Aktiv Müəllimlər"
                        value={teacherStatistics?.active_teachers || 0}
                        prefix={<UserOutlined style={{ color: '#52c41a' }} />}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card >
                      <Statistic
                        title="Aktiv Kurslar"
                        value={courseStatistics?.active_courses || 0}
                        prefix={<BookOutlined style={{ color: '#fa8c16' }} />}
                        valueStyle={{ color: '#fa8c16' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card >
                      <Statistic
                        title="Ödənişlər"
                        value={paymentStatistics?.total_payments || 0}
                        prefix={<RiseOutlined style={{ color: '#722ed1' }} />}
                        valueStyle={{ color: '#722ed1' }}
                      />
                    </Card>
                  </Col>
                </Row>

                {/* Performance Overview */}
                <Row gutter={[24, 24]} className="mb-8">
                  <Col xs={24} lg={8}>
                    <Card 
                      title={
                        <div className="flex items-center">
                          <PieChartOutlined className="mr-2 text-blue-500" />
                          <span>Performans Göstəriciləri</span>
                        </div>
                      } 
                      className=" h-full"
                    >
                      <ResponsiveContainer width="100%" height={250}>
                        <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="90%" data={performanceData}>
                          <RadialBar
                            label={{ position: 'insideStart', fill: '#fff' }}
                            background
                            dataKey="value"
                          />
                          <Legend iconSize={10} layout="vertical" verticalAlign="bottom" />
                          <Tooltip formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Faiz']} />
                        </RadialBarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                  
                  <Col xs={24} lg={16}>
                    <Card 
                      title={
                        <div className="flex items-center">
                          <LineChartOutlined className="mr-2 text-green-500" />
                          <span>Aylıq Artım Trendi</span>
                        </div>
                      } 
                      className=" h-full"
                    >
                      <ResponsiveContainer width="100%" height={250}>
                        <AreaChart data={monthlyData}>
                          <defs>
                            <linearGradient id="colorStudents" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#1890ff" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="#1890ff" stopOpacity={0.1}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #d9d9d9',
                              borderRadius: '6px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="students" 
                            stroke="#1890ff" 
                            fillOpacity={1} 
                            fill="url(#colorStudents)"
                            strokeWidth={3}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                </Row>

                {/* Enhanced Chartlar */}
                <Row gutter={[24, 24]} className="mb-8">
                  {/* Status Chart */}
                  <Col xs={24} lg={12}>
                    <Card 
                      title={
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <BarChartOutlined className="mr-2 text-orange-500" />
                            <span>Tələbə Status Analizi</span>
                          </div>
                          <Tag color="orange">Canlı Məlumat</Tag>
                        </div>
                      } 
                      className=""
                    >
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={statusData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip 
                            formatter={(value) => [value, 'Tələbə sayı']}
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #d9d9d9',
                              borderRadius: '6px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Bar 
                            dataKey="value" 
                            radius={[4, 4, 0, 0]}
                            fill="#8884d8"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                      
                      {/* Status summary */}
                      <div className="mt-4 grid grid-cols-3 gap-4">
                        {statusData.map((item, index) => (
                          <div key={index} className="text-center p-3 rounded-lg" style={{ backgroundColor: `${item.fill}15` }}>
                            <div className="text-2xl font-bold" style={{ color: item.fill }}>
                              {item.value}
                            </div>
                            <div className="text-sm text-gray-600">{item.name}</div>
                            <div className="text-xs text-gray-500">{item.percentage}%</div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </Col>
                </Row>

                {/* Comparison Chart - İndi Aylıq Gəlir Chart-ı */}
                <Row gutter={[24, 24]}>
                  <Col xs={24}>
                    <Card 
                      title={
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <RiseOutlined className="mr-2 text-green-500" />
                            <span>Aylıq Gəlir Statistikası</span>
                          </div>
                          <Tag color="green">AZN</Tag>
                        </div>
                      } 
                      className=""
                    >
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={monthlyRevenueData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                          <YAxis 
                            tick={{ fontSize: 12 }} 
                            tickFormatter={(value) => {
                              // Dinamik formatlaşdırma - böyük rəqəmlər üçün
                              if (value >= 1000000) {
                                return `${(value / 1000000).toFixed(1)}M`;
                              } else if (value >= 1000) {
                                return `${(value / 1000).toFixed(1)}K`;
                              }
                              return value.toString();
                            }}
                          />
                          <Tooltip 
                            formatter={(value) => {
                              const numValue = Number(value);
                              // Dinamik formatlaşdırma tooltip üçün
                              if (numValue >= 1000000) {
                                return [`${(numValue / 1000000).toFixed(2)}M AZN`, 'Gəlir'];
                              } else if (numValue >= 1000) {
                                return [`${(numValue / 1000).toFixed(2)}K AZN`, 'Gəlir'];
                              }
                              return [`${numValue.toFixed(2)} AZN`, 'Gəlir'];
                            }}
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #d9d9d9',
                              borderRadius: '6px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          />
                          <Legend />
                          <Bar 
                            dataKey="revenue" 
                            fill="#52c41a" 
                            name="Aylıq Gəlir (AZN)" 
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                      
                      {/* Gəlir xülasəsi - dinamik formatlaşdırma ilə */}
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 rounded-lg bg-green-50">
                          <div className="text-2xl font-bold text-green-600">
                            {(() => {
                              const total = monthlyRevenueData.reduce((sum, item) => sum + item.revenue, 0);
                              if (total >= 1000000) {
                                return `${(total / 1000000).toFixed(2)}M`;
                              } else if (total >= 1000) {
                                return `${(total / 1000).toFixed(2)}K`;
                              }
                              return total.toFixed(2);
                            })()}
                          </div>
                          <div className="text-sm text-gray-600">İllik Ümumi</div>
                          <div className="text-xs text-gray-500">AZN</div>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-blue-50">
                          <div className="text-2xl font-bold text-blue-600">
                            {(() => {
                              const activeMonths = monthlyRevenueData.filter(item => item.revenue > 0).length || 1;
                              const average = monthlyRevenueData.reduce((sum, item) => sum + item.revenue, 0) / activeMonths;
                              if (average >= 1000000) {
                                return `${(average / 1000000).toFixed(2)}M`;
                              } else if (average >= 1000) {
                                return `${(average / 1000).toFixed(2)}K`;
                              }
                              return average.toFixed(2);
                            })()}
                          </div>
                          <div className="text-sm text-gray-600">Orta Aylıq</div>
                          <div className="text-xs text-gray-500">AZN</div>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-purple-50">
                          <div className="text-2xl font-bold text-purple-600">
                            {(() => {
                              const max = Math.max(...monthlyRevenueData.map(item => item.revenue));
                              if (max >= 1000000) {
                                return `${(max / 1000000).toFixed(2)}M`;
                              } else if (max >= 1000) {
                                return `${(max / 1000).toFixed(2)}K`;
                              }
                              return max.toFixed(2);
                            })()}
                          </div>
                          <div className="text-sm text-gray-600">Ən Yüksək</div>
                          <div className="text-xs text-gray-500">AZN</div>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-orange-50">
                          <div className="text-2xl font-bold text-orange-600">
                            {monthlyRevenueData.filter(item => item.revenue > 0).length}
                          </div>
                          <div className="text-sm text-gray-600">Aktiv Aylar</div>
                          <div className="text-xs text-gray-500">Say</div>
                        </div>
                      </div>
                    </Card>
                  </Col>
                </Row>
              </>
            ) : (
              <div className="text-center py-16">
                <div className="mb-6">
                  <BarChartOutlined className="text-6xl text-gray-300" />
                </div>
                <Title level={3} className="mb-4 text-gray-500">Şirkət seçin</Title>
                <Text type="secondary" className="text-lg">
                  Statistikaları və chartları görmək üçün yuxarıdan şirkət seçin.
                </Text>
              </div>
            )}
          </div>
        );
      case 'settings':
        return (
          <div>
            <Title level={2} className="mb-6">
              <SettingOutlined className="mr-2" />
              Parametrlər
            </Title>
            
            <Row gutter={[24, 24]}>
              {/* Sistem Ayarları Bölməsi - Yalnız admin istifadəçilər üçün */}
              {user.is_superuser && (
                <Col xs={24}>
                  <Card title="Sistem Ayarları" className="shadow-sm">
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <div className="space-y-4">
                          <div>
                            <Text strong className="block mb-2">Sistem Logosu:</Text>
                            <div className="flex items-center space-x-4">
                              <Avatar
                                size={64}
                                src={systemSettings?.logo_url}
                                className="border"
                              >
                                {!systemSettings?.logo_url && (systemSettings?.system_name?.charAt(0) || 'C')}
                              </Avatar>
                              <div>
                                <Upload
                                  beforeUpload={handleLogoSelect}
                                  showUploadList={false}
                                  accept="image/*"
                                >
                                  <Button 
                                    icon={<UploadOutlined />} 
                                    loading={logoUploading}
                                  >
                                    Logo Yüklə
                                  </Button>
                                </Upload>
                                {logoFile && (
                                  <div className="mt-2">
                                    <Text className="text-sm text-gray-600">
                                      Seçildi: {logoFile.name}
                                    </Text>
                                    <div className="mt-1">
                                      <Button 
                                        type="primary" 
                                        size="small"
                                        onClick={() => handleLogoUpload(logoFile)}
                                        loading={logoUploading}
                                      >
                                        Yüklə
                                      </Button>
                                      <Button 
                                        size="small" 
                                        className="ml-2"
                                        onClick={() => setLogoFile(null)}
                                      >
                                        Ləğv et
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <Text strong className="block mb-2">Sistem Adı:</Text>
                            <Text>{systemSettings?.system_name || 'CRM Sistemi'}</Text>
                          </div>
                          
                          {systemSettings?.company_name && (
                            <div>
                              <Text strong className="block mb-2">Şirkət Adı:</Text>
                              <Text>{systemSettings.company_name}</Text>
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              )}

              {/* Ödəniş Bildirişləri Bölməsi */}
              <Col xs={24}>
                <Card title="Ödəniş Bildirişləri" className="shadow-sm">
                  <div className="space-y-8">
                    
                    {/* SMS - Ödənişdən əvvəl */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          <Title level={5} className="mb-0">Ödənişdən əvvəl (SMS)</Title>
                        </div>
                        <Button 
                          type={tempNotificationSettings.smsBeforePayment.active ? "primary" : "default"}
                          size="small"
                          onClick={() => setTempNotificationSettings({
                            ...tempNotificationSettings,
                            smsBeforePayment: {
                              ...tempNotificationSettings.smsBeforePayment,
                              active: !tempNotificationSettings.smsBeforePayment.active
                            }
                          })}
                        >
                          {tempNotificationSettings.smsBeforePayment.active ? "Aktiv" : "Passiv"}
                        </Button>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <Text>Ödənişdən</Text>
                          <Input 
                            type="number"
                            value={tempNotificationSettings.smsBeforePayment.daysBefore}
                            onChange={(e) => setTempNotificationSettings({
                              ...tempNotificationSettings,
                              smsBeforePayment: {
                                ...tempNotificationSettings.smsBeforePayment,
                                daysBefore: parseInt(e.target.value) || 1
                              }
                            })}
                            style={{ width: 80 }}
                            min={1}
                            max={30}
                          />
                          <Text>gün əvvəl</Text>
                        </div>
                        
                        <div>
                          <Text strong className="block mb-2">Mesaj məzmunu:</Text>
                          <Input.TextArea
                            value={tempNotificationSettings.smsBeforePayment.message}
                            onChange={(e) => setTempNotificationSettings({
                              ...tempNotificationSettings,
                              smsBeforePayment: {
                                ...tempNotificationSettings.smsBeforePayment,
                                message: e.target.value
                              }
                            })}
                            rows={3}
                            placeholder="SMS mesajı yazın..."
                            showCount
                            maxLength={765}
                          />
                          <div className="mt-2 space-y-1">
                            <div className="flex justify-between text-xs">
                              <Text type="secondary">
                                SMS sayı: {Math.ceil(tempNotificationSettings.smsBeforePayment.message.length / 160)} SMS
                              </Text>
                              <Text type="secondary">
                                {tempNotificationSettings.smsBeforePayment.message.length <= 160 ? '1 SMS (160 simvol)' :
                                 tempNotificationSettings.smsBeforePayment.message.length <= 306 ? '2 SMS (306 simvol)' :
                                 tempNotificationSettings.smsBeforePayment.message.length <= 459 ? '3 SMS (459 simvol)' :
                                 tempNotificationSettings.smsBeforePayment.message.length <= 612 ? '4 SMS (612 simvol)' :
                                 '5 SMS (765 simvol)'}
                              </Text>
                            </div>
                            <Text type="warning" className="text-xs block">
                              ⚠️ Göndəriləcək mesaj sayı müştəri sayına vurularaq SMS balansınızdan çıxılacaq!
                            </Text>
                            <Text type="secondary" className="text-xs block">
                              💡 İngilis hərfləri ilə yazılması tövsiyyə olunur. Ş→sh, Ç→ch kimi əvəzlənir.
                            </Text>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* WhatsApp - Ödənişdən əvvəl */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <Title level={5} className="mb-0">Ödənişdən əvvəl (WhatsApp)</Title>
                        </div>
                        <Button 
                          type={tempNotificationSettings.whatsappBeforePayment.active ? "primary" : "default"}
                          size="small"
                          onClick={() => setTempNotificationSettings({
                            ...tempNotificationSettings,
                            whatsappBeforePayment: {
                              ...tempNotificationSettings.whatsappBeforePayment,
                              active: !tempNotificationSettings.whatsappBeforePayment.active
                            }
                          })}
                        >
                          {tempNotificationSettings.whatsappBeforePayment.active ? "Aktiv" : "Passiv"}
                        </Button>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <Text>Ödənişdən</Text>
                          <Input 
                            type="number"
                            value={tempNotificationSettings.whatsappBeforePayment.daysBefore}
                            onChange={(e) => setTempNotificationSettings({
                              ...tempNotificationSettings,
                              whatsappBeforePayment: {
                                ...tempNotificationSettings.whatsappBeforePayment,
                                daysBefore: parseInt(e.target.value) || 1
                              }
                            })}
                            style={{ width: 80 }}
                            min={1}
                            max={30}
                          />
                          <Text>gün əvvəl</Text>
                        </div>
                        
                        <div>
                          <Text strong className="block mb-2">Mesaj məzmunu:</Text>
                          <Input.TextArea
                            value={tempNotificationSettings.whatsappBeforePayment.message}
                            onChange={(e) => setTempNotificationSettings({
                              ...tempNotificationSettings,
                              whatsappBeforePayment: {
                                ...tempNotificationSettings.whatsappBeforePayment,
                                message: e.target.value
                              }
                            })}
                            rows={4}
                            placeholder="WhatsApp mesajı yazın..."
                            showCount
                          />
                        </div>
                      </div>
                    </div>

                    {/* SMS - Ödəniş günü */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                          <Title level={5} className="mb-0">Ödəniş günü (SMS)</Title>
                        </div>
                        <Button 
                          type={tempNotificationSettings.smsOnPaymentDay.active ? "primary" : "default"}
                          size="small"
                          onClick={() => setTempNotificationSettings({
                            ...tempNotificationSettings,
                            smsOnPaymentDay: {
                              ...tempNotificationSettings.smsOnPaymentDay,
                              active: !tempNotificationSettings.smsOnPaymentDay.active
                            }
                          })}
                        >
                          {tempNotificationSettings.smsOnPaymentDay.active ? "Aktiv" : "Passiv"}
                        </Button>
                      </div>
                      
                      <div>
                        <Text strong className="block mb-2">Mesaj məzmunu:</Text>
                        <Input.TextArea
                          value={tempNotificationSettings.smsOnPaymentDay.message}
                          onChange={(e) => setTempNotificationSettings({
                            ...tempNotificationSettings,
                            smsOnPaymentDay: {
                              ...tempNotificationSettings.smsOnPaymentDay,
                              message: e.target.value
                            }
                          })}
                          rows={3}
                          placeholder="SMS mesajı yazın..."
                          showCount
                          maxLength={765}
                        />
                        <div className="mt-2 space-y-1">
                          <div className="flex justify-between text-xs">
                            <Text type="secondary">
                              SMS sayı: {Math.ceil(tempNotificationSettings.smsOnPaymentDay.message.length / 160)} SMS
                            </Text>
                            <Text type="secondary">
                              {tempNotificationSettings.smsOnPaymentDay.message.length <= 160 ? '1 SMS (160 simvol)' :
                               tempNotificationSettings.smsOnPaymentDay.message.length <= 306 ? '2 SMS (306 simvol)' :
                               tempNotificationSettings.smsOnPaymentDay.message.length <= 459 ? '3 SMS (459 simvol)' :
                               tempNotificationSettings.smsOnPaymentDay.message.length <= 612 ? '4 SMS (612 simvol)' :
                               '5 SMS (765 simvol)'}
                            </Text>
                          </div>
                          <Text type="warning" className="text-xs block">
                            ⚠️ Göndəriləcək mesaj sayı müştəri sayına vurularaq SMS balansınızdan çıxılacaq!
                          </Text>
                          <Text type="secondary" className="text-xs block">
                            💡 İngilis hərfləri ilə yazılması tövsiyyə olunur. Ş→sh, Ç→ch kimi əvəzlənir.
                          </Text>
                        </div>
                      </div>
                    </div>

                    {/* WhatsApp - Ödəniş günü */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                          <Title level={5} className="mb-0">Ödəniş günü (WhatsApp)</Title>
                        </div>
                        <Button 
                          type={tempNotificationSettings.whatsappOnPaymentDay.active ? "primary" : "default"}
                          size="small"
                          onClick={() => setTempNotificationSettings({
                            ...tempNotificationSettings,
                            whatsappOnPaymentDay: {
                              ...tempNotificationSettings.whatsappOnPaymentDay,
                              active: !tempNotificationSettings.whatsappOnPaymentDay.active
                            }
                          })}
                        >
                          {tempNotificationSettings.whatsappOnPaymentDay.active ? "Aktiv" : "Passiv"}
                        </Button>
                      </div>
                      
                      <div>
                        <Text strong className="block mb-2">Mesaj məzmunu:</Text>
                        <Input.TextArea
                          value={tempNotificationSettings.whatsappOnPaymentDay.message}
                          onChange={(e) => setTempNotificationSettings({
                            ...tempNotificationSettings,
                            whatsappOnPaymentDay: {
                              ...tempNotificationSettings.whatsappOnPaymentDay,
                              message: e.target.value
                            }
                          })}
                          rows={4}
                          placeholder="WhatsApp mesajı yazın..."
                          showCount
                        />
                      </div>
                    </div>

                    {/* Test Mesaj Bölməsi */}
                    {/* <div className="bg-gray-50 rounded-lg p-4">
                      <Title level={5}>Test Mesajı</Title>
                      <Text type="secondary" className="block mb-3">
                        Ayarları test etmək üçün özünüzə test mesajı göndərə bilərsiniz
                      </Text>
                      <div className="flex space-x-2">
                        <Button type="default" onClick={testSMS}>SMS Test Et</Button>
                        <Button type="default" onClick={testWhatsApp}>WhatsApp Test Et</Button>
                      </div>
                    </div> */}

                    {/* Saxla/Ləğv et düymələri */}
                    {hasNotificationChanges && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Text strong className="text-blue-900">Saxlanmamış dəyişikliklər var</Text>
                            <Text className="text-blue-700 text-sm block">
                              Dəyişikliklərinizi saxlamaq və ya ləğv etmək üçün düymələrdən birini seçin
                            </Text>
                          </div>
                          <div className="flex space-x-2">
                            <Button onClick={cancelNotificationChanges}>
                              Ləğv et
                            </Button>
                            <Button type="primary" onClick={saveNotificationSettings}>
                              Saxla
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        );
      default:
        return (
          <div className="text-center py-12">
            <Title level={2} className="mb-4">
              {menuItems.find(item => item.key === activeTab)?.label}
            </Title>
            <Text type="secondary" className="text-lg">
              Bu bölmə hazırlanmaqdadır...
            </Text>
          </div>
        );
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }} className={isDarkMode ? 'dark' : ''}>
      <Sider 
        collapsible 
        collapsed={collapsed} 
        onCollapse={setCollapsed}
        theme={isDarkMode ? "dark" : "light"}
        width={250}
        className="relative"
        trigger={null}
      >
        <div className="flex justify-center border-b" style={{padding:'12px'}}>
          <SystemLogo 
            size={collapsed ? 32 : 40} 
            showName={!collapsed} 
            selectedCompany={selectedCompany}
          />
        </div>
        
        <Menu
          mode="inline"
          selectedKeys={[activeTab]}
          items={menuItems}
          onClick={({ key }) => handleTabChange(key)}
          className="border-r-0"
          style={{ marginBottom: '120px' }}
        />
        
        {/* Profil və Trigger birlikdə - fixed position */}
        <div className="fixed bottom-0 left-0 bg-white border-t" style={{ width: collapsed ? '80px' : '250px' }}>
          {/* Profil hissəsi */}
          <div className="p-4 border-b">
            {!collapsed ? (
              <Dropdown menu={{ items: userMenuItems }} placement="topRight">
                <div className="flex items-center p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <Avatar 
                    size="small" 
                    src={user.profile_image ? `${user.profile_image}?t=${Date.now()}` : undefined}
                    className="mr-3"
                    onError={() => {
                      return false;
                    }}
                  >
                    {!user.profile_image && user.first_name.charAt(0).toUpperCase()}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user.first_name} {user.last_name}
                    </div>
                  </div>
                </div>
              </Dropdown>
            ) : (
              <Dropdown menu={{ items: userMenuItems }} placement="topRight">
                <div className="flex justify-center">
                  <Avatar 
                    size="small" 
                    src={user.profile_image ? `${user.profile_image}?t=${Date.now()}` : undefined}
                    className="cursor-pointer hover:ring-2 hover:ring-blue-200 transition-all"
                    onError={() => {
                      return false;
                    }}
                  >
                    {!user.profile_image && user.first_name.charAt(0).toUpperCase()}
                  </Avatar>
                </div>
              </Dropdown>
            )}
          </div>
          
          {/* Trigger hissəsi */}
          <div 
            className="flex items-center justify-center py-3 px-4 bg-white hover:bg-gray-50 transition-all duration-300 cursor-pointer group"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? (
              <>
                <MenuUnfoldOutlined className="text-gray-600 group-hover:text-blue-500 text-base transition-colors" />
              </>
            ) : (
              <>
                <MenuFoldOutlined className="text-gray-600 group-hover:text-blue-500 mr-2 text-base transition-colors" />
                <span className="text-gray-600 group-hover:text-blue-500 text-sm font-medium transition-colors">Kiçilt</span>
              </>
            )}
          </div>
        </div>
      </Sider>

      <Layout>
        <Header className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white'} px-6 flex items-center justify-between shadow-sm`}>
          <div className="flex items-center space-x-4">
            <Title level={4} className={`m-0 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              CRM İdarəetmə Paneli
            </Title>
          </div>
          
          {/* Sağ tərəf - Notification və Şirkət Seçici */}
          
            {/* Notification Icon */}
          
          {/* Sağ tərəf - İl Seçici, Notification və Şirkət Seçici */}
          <div className="flex items-center space-x-4">
            {/* İl Seçici */}
            <DatePicker
              picker="year"
              value={dayjs().year(selectedYear || new Date().getFullYear())}
              onChange={(date) => {
                const year = date ? date.year() : new Date().getFullYear();
                setSelectedYear(year);
              }}
              placeholder="İl seçin"
              allowClear={false}
              className="w-32"
              size="large"
            />

            {/* Notification Icon */}
            <Button 
              type="text" 
              icon={<BellOutlined />} 
              className="relative hover:bg-gray-100 rounded-full"
              size="large"
              onClick={() => handleTabChange('settings')}
            />

            {/* Şirkət Seçici - Bina İkonu ilə */}
            {userCompanies.length > 0 && (
              <Dropdown
                menu={{
                  items: [
                    // İstifadəçinin şirkətləri
                    ...userCompanies.map(company => ({
                      key: `user-${company.id}`,
                      label: (
                        <div className="py-1">
                          <div className="font-medium flex items-center">
                            <span className="text-blue-500 mr-2">★</span>
                            {company.name}
                            {selectedCompany?.id === company.id && (
                              <Tag color="blue" className="ml-2" style={{ fontSize: '10px', padding: '0 4px' }}>Aktiv</Tag>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {company.role} • {'student_count' in company ? company.student_count : 0} tələbə • {company.city}
                          </div>
                        </div>
                      ),
                      onClick: () => handleCompanyChange(company.id)
                    })),
                    // Ayırıcı - yalnız admin üçün və digər şirkətlər varsa
                    ...(user.is_superuser && allCompanies.length > 0 && allCompanies.some(c => !userCompanies.some(uc => uc.id === c.id)) ? [{
                      type: 'divider' as const,
                      key: 'divider'
                    }] : []),
                    // Bütün şirkətlər - yalnız admin üçün
                    ...(user.is_superuser && allCompanies.length > 0 ? 
                      allCompanies
                        .filter(company => !userCompanies.some(uc => uc.id === company.id))
                        .map(company => ({
                          key: `all-${company.id}`,
                          label: (
                            <div className="py-1">
                              <div className="font-medium">
                                {company.name}
                                {selectedCompany?.id === company.id && (
                                  <Tag color="blue" className="ml-2" style={{ fontSize: '10px', padding: '0 4px' }}>Aktiv</Tag>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {company.company_type} • {'student_count' in company ? company.student_count : 0} tələbə • {company.city}
                              </div>
                            </div>
                          ),
                          onClick: () => handleCompanyChange(company.id)
                        }))
                      : [])
                  ]
                }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button 
                  type="text" 
                  className="hover:bg-gray-100 rounded-full flex items-center"
                  size="large"
                  title={selectedCompany ? selectedCompany.name : 'Şirkət seçin'}
                >
                  <BankOutlined style={{ fontSize: '18px' }} />
                  {selectedCompany && (
                    <span className="ml-2 text-sm font-medium max-w-[150px] truncate">
                      {selectedCompany.name}
                    </span>
                  )}
                </Button>
              </Dropdown>
            )}
          </div>
        </Header>

        <Content className={`p-6 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          {renderContent()}
        </Content>
      </Layout>

      {/* Profil Edit Modal */}
      <Modal
        title="Profili Redaktə Et"
        open={isProfileModalVisible}
        onCancel={() => {
          setIsProfileModalVisible(false);
          setProfileImageFile(null);
          profileForm.resetFields();
        }}
        onOk={() => profileForm.submit()}
        okText="Yadda Saxla"
        cancelText="Ləğv Et"
        width={600}
      >
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={handleProfileUpdate}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Ad"
                name="first_name"
                rules={[{ required: true, message: 'Ad daxil edin' }]}
              >
                <Input placeholder="Adınızı daxil edin" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Soyad"
                name="last_name"
                rules={[{ required: true, message: 'Soyad daxil edin' }]}
              >
                <Input placeholder="Soyadınızı daxil edin" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Email daxil edin' },
              { type: 'email', message: 'Düzgün email daxil edin' }
            ]}
          >
            <Input placeholder="Email ünvanınızı daxil edin" />
          </Form.Item>

          <Form.Item label="Profil Şəkli">
            <div className="flex items-center space-x-4">
              <Avatar 
                size={64} 
                src={profileImageFile ? URL.createObjectURL(profileImageFile) : user.profile_image}
                className="border-2 border-gray-200"
              >
                {!user.profile_image && !profileImageFile && user.first_name.charAt(0).toUpperCase()}
              </Avatar>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleProfileImageSelect(file);
                    }
                  }}
                  style={{ display: 'none' }}
                  id="profile-image-input"
                />
                <Button
                  icon={<CameraOutlined />}
                  onClick={() => document.getElementById('profile-image-input')?.click()}
                >
                  Şəkil Seç
                </Button>
                {profileImageFile && (
                  <div className="mt-2 text-sm text-green-600">
                    Seçilmiş fayl: {profileImageFile.name}
                  </div>
                )}
              </div>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default Dashboard;